import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ArchiveTeamsConfirmationModal } from '../archive-teams-confirmation-modal';
import { useAppendCleanUpTeamsFollowUp } from '../hooks/use-append-clean-up-teams-follow-up';
import type { AtlassianTeamUpdateArchiveHydratedAction } from '../types';
import type { TeamV2 } from '../types';
import {
	isActionResolved,
	isActionErrored,
	isActionFinished,
	isActionReadonly,
} from '../utils/action-state';
import {
	buildHydratedArchiveTeamActions,
	sortHydratedArchiveTeamActions,
} from '../utils/build-hydrated-archive-team-actions';
import { TEAM_MEMBERS_PREVIEW_QUERY_FIRST } from '../utils/team-members-preview';

import type { archiveTeamsCardQuery } from './__generated__/archiveTeamsCardQuery.graphql';
import { ArchiveTeamsCardListView } from './archive-teams-card-list-view';
import { ArchiveTeamsCardSkeleton } from './archive-teams-card-skeleton';
import { ArchiveTeamsCardTeamView } from './archive-teams-card-team-view';
import { CardWrapper } from './card-wrapper';
import messages from './messages';

type ArchiveTeamsCardContentProps = Omit<
	ActionRendererFor<typeof ActionKeys.AtlassianTeamUpdateArchive>,
	'actions'
> & {
	actions: AtlassianTeamUpdateArchiveHydratedAction[];
};

const ArchiveTeamsCardContent = ({
	actions,
	cloudId,
	conversationId,
	onConfirm,
	getReadonly,
	getStatus,
	onCancel,
}: ArchiveTeamsCardContentProps) => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const [actionsToArchive, setActionsToArchive] = useState<
		AtlassianTeamUpdateArchiveHydratedAction[]
	>([]);
	const [actionsCurrentlyLoading, setActionsCurrentlyLoading] = useState<string[]>([]);
	const [selectedTeam, setSelectedTeam] = useState<AtlassianTeamUpdateArchiveHydratedAction | null>(
		null,
	);

	const visibleTeams = useMemo(
		() => actions.filter((action) => !isActionResolved(action, getStatus)),
		[actions, getStatus],
	);

	const erroredTeams = useMemo(
		() => actions.filter((action) => isActionErrored(action, getStatus)),
		[actions, getStatus],
	);

	const archivedTeams = useMemo(
		() => actions.filter((action) => isActionFinished(action, getStatus)),
		[actions, getStatus],
	);

	const allActionsAreReadonly = actions.every((action) => isActionReadonly(action, getReadonly));
	const allActionsResolvedSuccessfully = actions.every((action) =>
		isActionResolved(action, getStatus),
	);

	const { maybeAppendFollowUp } = useAppendCleanUpTeamsFollowUp({
		conversationId,
		actions,
		getReadonly,
	});

	const subtitle = useMemo(() => {
		if (actionsCurrentlyLoading.length > 0) {
			return formatMessage(messages.archiving, {
				teamCount: actionsCurrentlyLoading.length,
			});
		}
		if (allActionsAreReadonly && erroredTeams.length > 0) {
			return (
				<Text color={'color.text.warning'}>
					{formatMessage(messages.couldNotArchive, {
						teamCount: erroredTeams.length,
					})}
				</Text>
			);
		}
		return undefined;
	}, [actionsCurrentlyLoading.length, erroredTeams.length, formatMessage, allActionsAreReadonly]);

	const handleArchivingChange = useCallback(
		(teamIds: string[]) => {
			setActionsCurrentlyLoading(teamIds);
		},
		[setActionsCurrentlyLoading],
	);

	const handleRemoveTeam = useCallback(
		async (team: AtlassianTeamUpdateArchiveHydratedAction) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'removeTeamFromCleanUpTeams',
				attributes: {
					teamId: team.data.teamId,
				},
			});
			await onCancel(team);
			setSelectedTeam((current) => (current?.data.teamId === team.data.teamId ? null : current));
			maybeAppendFollowUp([team]);
		},
		[onCancel, sendAnalyticsEvent, maybeAppendFollowUp],
	);

	const handleArchiveTeam = useCallback(
		(teams: AtlassianTeamUpdateArchiveHydratedAction[]) => {
			if (teams.length > 0) {
				sendAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'setTeamsToArchiveCleanUpTeams',
					attributes: {
						teamCount: teams.length,
					},
				});
			}
			setActionsToArchive(teams);
		},
		[sendAnalyticsEvent, setActionsToArchive],
	);

	const handleViewTeamCard = useCallback(
		(team: AtlassianTeamUpdateArchiveHydratedAction) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewTeamCardCleanUpTeams',
				attributes: {
					teamId: team.data.teamId,
				},
			});
			setSelectedTeam(team);
		},
		[sendAnalyticsEvent, setSelectedTeam],
	);

	if (allActionsResolvedSuccessfully) {
		return (
			<CardWrapper
				key="archived-teams-success-card"
				testId="archive-teams-success-card"
				subtitle={
					archivedTeams.length > 0 &&
					formatMessage(messages.archivedSuccess, {
						teamCount: archivedTeams.length,
					})
				}
				headerActionOverride={<StatusSuccessIcon label="" color={token('color.icon.success')} />}
			/>
		);
	}

	return (
		<CardWrapper isExpandable={true} subtitle={subtitle} testId="archive-teams-card">
			{selectedTeam ? (
				<ArchiveTeamsCardTeamView
					team={selectedTeam}
					onBack={() => setSelectedTeam(null)}
					onRemove={handleRemoveTeam}
					onArchive={handleArchiveTeam}
					cloudId={cloudId}
					isReadonly={isActionReadonly(selectedTeam, getReadonly)}
				/>
			) : (
				<ArchiveTeamsCardListView
					teams={visibleTeams}
					onSelectTeam={handleViewTeamCard}
					onRemoveTeam={handleRemoveTeam}
					getStatus={getStatus}
					onArchive={handleArchiveTeam}
					actionsCurrentlyLoading={actionsCurrentlyLoading}
					isReadOnly={allActionsAreReadonly}
				/>
			)}
			<ArchiveTeamsConfirmationModal
				actions={actionsToArchive}
				onConfirm={onConfirm}
				onArchiveComplete={() => {
					setSelectedTeam(null);
					maybeAppendFollowUp(actionsToArchive);
				}}
				onClose={() => setActionsToArchive([])}
				onArchivingChange={handleArchivingChange}
			/>
		</CardWrapper>
	);
};

function ArchiveTeamsCardWithHydration(
	props: ActionRendererFor<typeof ActionKeys.AtlassianTeamUpdateArchive>,
) {
	const teamIds = useMemo(
		() =>
			props.actions.map((a) => a.data.teamId).filter((id): id is string => id != null && id !== ''),
		[props.actions],
	);

	const data = useLazyLoadQuery<archiveTeamsCardQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- data merged via buildHydratedArchiveTeamActions and passed to TeamItem/TeamCard */
		graphql`
			query archiveTeamsCardQuery($teamIds: [ID]!, $siteId: String!, $membersCount: Int!) {
				team {
					teamsV2(ids: $teamIds, siteId: $siteId) @optIn(to: "Team") {
						id
						displayName
						description
						state
						membershipSettings
						organizationId
						isVerified
						largeAvatarImageUrl
						type {
							name
						}
						members(first: $membersCount) {
							edges {
								node {
									member {
										accountId
										name
										picture
									}
								}
							}
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			teamIds,
			siteId: props.cloudId,
			membersCount: TEAM_MEMBERS_PREVIEW_QUERY_FIRST,
		},
		{ fetchPolicy: 'store-or-network' },
	);

	const teamsV2Map = useMemo(() => {
		const map = new Map<string, TeamV2>();
		for (const team of data.team?.teamsV2 ?? []) {
			if (!team?.id) continue;
			const { id, ...teamWithoutId } = team;
			map.set(id, teamWithoutId);
		}
		return map;
	}, [data.team?.teamsV2]);

	const enrichedActions = useMemo(() => {
		const hydrated = buildHydratedArchiveTeamActions(props.actions, teamsV2Map);
		return sortHydratedArchiveTeamActions(hydrated);
	}, [props.actions, teamsV2Map]);

	return <ArchiveTeamsCardContent {...props} actions={enrichedActions} />;
}

export function ArchiveTeamsCard(
	props: ActionRendererFor<typeof ActionKeys.AtlassianTeamUpdateArchive>,
): React.JSX.Element {
	return (
		<Suspense fallback={<ArchiveTeamsCardSkeleton />}>
			<ArchiveTeamsCardWithHydration {...props} />
		</Suspense>
	);
}
